import React, { useEffect, useState, useMemo, useContext } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Modal, Divider } from 'antd';
import { useHistory } from 'react-router-dom';
import waitForStoreUpdate from '@utils/waitForStoreUpdate';
import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import MessageButton from '@components/MessageButton';
import { fetchForms, deleteForms } from '@redux/forms/actions';
import { selectFormDeleting } from '@redux/forms/reducers';
import { useTranslation } from 'react-i18next';
import SearchableTable from '@components/SearchableTable';
import IconButton from '@components/IconButton';
import { Permissions } from '@authorisation/constants';
import { useHasPermissions } from '@authorisation/hooks';
import { FormSelectorContext } from '../../../pages/Forms';
import createColumns from './createColumns';
import ContentRef from '@utils/contentRef';

const confirmDelete = (onOk, t, formTranslationKey, count) => {
  Modal.confirm({
    title: t(`${formTranslationKey}:FormList.confirmDelete`, { count }),
    onOk,
    onCancel: () => {},
  });
};

const filterFunc = (value, comparator) => {
  return comparator(value.name) || comparator(value.description);
};

const FormListCard = ({ showTitle, match, formTranslationKey }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const store = useStore();
  const selector = useContext(FormSelectorContext);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowsContent, setSelectedRowsContent] = useState([]);
  const columns = useMemo(() => createColumns(t, formTranslationKey), [t, formTranslationKey]);

  useEffect(() => {
    dispatch(fetchForms());
  }, [dispatch]);

  const [loading, forms] = useSelector(selector);

  const { [Permissions.ViewPatients]: canViewPatients } = useHasPermissions(
    Permissions.ViewPatients,
  );

  return (
    <SearchableTable
      columns={columns}
      dataSource={forms}
      rowKey="content"
      extra={
        <>
          {canViewPatients ? (
            <>
              <MessageButton
                disabled={
                  selectedRows.length !== 1 ||
                  selectedRowsContent.map(content => content.audienceType).includes('clinician')
                }
                onClick={() => {
                  const ref = ContentRef.fromString(selectedRows[0]);
                  const form = forms.find(f => ref.matches(f.content, { ignoreVersion: true }))
                    ?.uuid;
                  history.push(`/messages/new/${form}`);
                }}
              />
              <Divider key="divider" type="vertical" />
            </>
          ) : null}
          <IconButton
            disabled={!selectedRows.length}
            icon={faTrashAlt}
            tooltip={t('common:buttons.delete')}
            onClick={() =>
              confirmDelete(
                () => {
                  const formIds = selectedRows
                    .map(content => {
                      const ref = ContentRef.fromString(content);
                      return forms.find(f => ref.matches(f.content, { ignoreVersion: true }))?.uuid;
                    })
                    .filter(Boolean);
                  dispatch(deleteForms(formIds, formTranslationKey));
                  setSelectedRows([]);
                  return waitForStoreUpdate(store, selectFormDeleting); // must return for modal to be dismissed
                },
                t,
                formTranslationKey,
                selectedRows.length,
              )
            }
          />
        </>
      }
      filterFunc={filterFunc}
      hasRoutedPagination
      loading={loading}
      title={showTitle ? t('cards:FormList.title') : null}
      onRowSelectionChange={(selectedRowKeys, selectedRows) => {
        setSelectedRows(selectedRowKeys);
        setSelectedRowsContent(selectedRows);
      }}
    />
  );
};

export default FormListCard;
